import { supabase } from './supabase';

export const studioService = {
  // Get all studios (admin function)
  async getAllStudios() {
    try {
      const { data, error } = await supabase
        .from('gaming_studios')
        .select(`
          *,
          games(count),
          owner:user_profiles!gaming_studios_owner_id_fkey(
            id,
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching all studios:', error);
      return { data: null, error };
    }
  },

  // Get studio by owner ID (simple lookup)
  async getStudioByOwner(ownerId) {
    try {
      const { data, error } = await supabase
        .from('gaming_studios')
        .select('*')
        .eq('owner_id', ownerId)
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error fetching studio by owner:', error);
      return { data: null, error };
    }
  },

  // Get studio for user with email fallback (for dashboard)
  async getStudioForUser(userId) {
    try {
      // First, try by owner_id
      let { data: studio } = await supabase
        .from('gaming_studios')
        .select('*')
        .eq('owner_id', userId)
        .single();

      if (studio) return { data: studio, error: null };

      // Fallback: Get user's email and check contact_email
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('id', userId)
        .single();

      if (profile?.email) {
        const { data: studioByEmail } = await supabase
          .from('gaming_studios')
          .select('*')
          .eq('contact_email', profile.email)
          .single();

        if (studioByEmail) {
          // Auto-fix: Update owner_id for future lookups
          await supabase
            .from('gaming_studios')
            .update({ owner_id: userId })
            .eq('id', studioByEmail.id);

          return { data: studioByEmail, error: null };
        }
      }

      return { data: null, error: null }; // No studio found
    } catch (error) {
      console.error('Error in getStudioForUser:', error);
      return { data: null, error };
    }
  },

  // Link studio to user (with ownership transfer and audit)
  async linkStudioToUser(studioId, newOwnerId, adminId) {
    try {
      // Get current studio info
      const { data: studio, error: studioError } = await supabase
        .from('gaming_studios')
        .select('*, owner:user_profiles!gaming_studios_owner_id_fkey(id, email, full_name)')
        .eq('id', studioId)
        .single();

      if (studioError) return { data: null, error: studioError };

      const previousOwnerId = studio.owner_id;

      // Log ownership change (audit trail)
      if (previousOwnerId && previousOwnerId !== newOwnerId) {
        await supabase.from('studio_ownership_changes').insert({
          studio_id: studioId,
          previous_owner_id: previousOwnerId,
          new_owner_id: newOwnerId,
          changed_by: adminId,
          reason: 'Admin reassignment'
        });

        // Demote previous owner if they don't own other studios
        const { data: otherStudios } = await supabase
          .from('gaming_studios')
          .select('id')
          .eq('owner_id', previousOwnerId)
          .neq('id', studioId);

        if (!otherStudios || otherStudios.length === 0) {
          await supabase
            .from('user_profiles')
            .update({ role: 'user' })
            .eq('id', previousOwnerId);
        }
      }

      // Update studio owner
      const { data: updatedStudio, error: updateError } = await supabase
        .from('gaming_studios')
        .update({ owner_id: newOwnerId })
        .eq('id', studioId)
        .select()
        .single();

      if (updateError) return { data: null, error: updateError };

      // Update new owner's role to studio_admin
      await supabase
        .from('user_profiles')
        .update({ role: 'studio_admin' })
        .eq('id', newOwnerId);

      return { data: updatedStudio, error: null, previousOwner: studio.owner };
    } catch (error) {
      console.error('Error linking studio to user:', error);
      return { data: null, error };
    }
  },

  // Create studio and auto-link to user by email
  async createStudioWithOwner(studioData, ownerEmail) {
    try {
      // Look up user by email
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('id, email, full_name')
        .eq('email', ownerEmail)
        .single();

      // Create the studio
      const studioPayload = {
        ...studioData,
        contact_email: ownerEmail,
        owner_id: userProfile?.id || null
      };

      const { data: newStudio, error: createError } = await supabase
        .from('gaming_studios')
        .insert([studioPayload])
        .select()
        .single();

      if (createError) return { data: null, error: createError, userLinked: false };

      // If user exists, update their role to studio_admin
      if (userProfile) {
        await supabase
          .from('user_profiles')
          .update({ role: 'studio_admin' })
          .eq('id', userProfile.id);

        return { data: newStudio, error: null, userLinked: true, user: userProfile };
      }

      // User doesn't exist yet - they'll be linked when they sign up
      return { data: newStudio, error: null, userLinked: false, pendingEmail: ownerEmail };
    } catch (error) {
      console.error('Error creating studio with owner:', error);
      return { data: null, error };
    }
  },

  // Get studio dashboard data
  async getStudioDashboard(studioId) {
    try {
      // Get games for this studio
      const { data: games, error: gamesError } = await supabase
        .from('games')
        .select('*')
        .eq('studio_id', studioId);

      if (gamesError) throw gamesError;

      // Get analytics summary
      const { data: analytics, error: analyticsError } = await supabase
        .from('game_analytics_summary')
        .select('*')
        .in('game_id', games.map(g => g.id))
        .order('date', { ascending: false })
        .limit(30);

      if (analyticsError) throw analyticsError;

      // Calculate totals
      const totalPlays = games.reduce((sum, game) => sum + (game.play_count || 0), 0);
      const activeGames = games.filter(game => game.status === 'published').length;

      return {
        data: {
          games,
          analytics: {
            totalPlays,
            totalRevenue: 0, // Would calculate from revenue tables
            activeGames,
            monthlyGrowth: 0 // Would calculate from analytics
          },
          recentActivity: [] // Would fetch from activity log
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching studio dashboard:', error);
      return { data: null, error };
    }
  },

  // Create new studio
  async createStudio(studioData) {
    try {
      const { data, error } = await supabase
        .from('gaming_studios')
        .insert([studioData])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error creating studio:', error);
      return { data: null, error };
    }
  },

  // Update studio
  async updateStudio(studioId, updates) {
    try {
      const { data, error } = await supabase
        .from('gaming_studios')
        .update(updates)
        .eq('id', studioId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error updating studio:', error);
      return { data: null, error };
    }
  },

  // Get API keys for studio
  async getAPIKeys(studioId) {
    try {
      const { data, error } = await supabase
        .from('studio_api_keys')
        .select('*')
        .eq('studio_id', studioId)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching API keys:', error);
      return { data: null, error };
    }
  },

  // Create API key
  async createAPIKey(studioId, keyData) {
    try {
      const { data, error } = await supabase
        .from('studio_api_keys')
        .insert([{
          studio_id: studioId,
          ...keyData
        }])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error creating API key:', error);
      return { data: null, error };
    }
  },

  // Revoke API key
  async revokeAPIKey(keyId) {
    try {
      const { data, error } = await supabase
        .from('studio_api_keys')
        .update({ status: 'revoked' })
        .eq('id', keyId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error revoking API key:', error);
      return { data: null, error };
    }
  },

  // Get domain whitelist
  async getDomainWhitelist(studioId) {
    try {
      const { data, error } = await supabase
        .from('studio_domain_whitelist')
        .select('*')
        .eq('studio_id', studioId)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching domain whitelist:', error);
      return { data: null, error };
    }
  },

  // Add domain to whitelist
  async addDomain(studioId, domain) {
    try {
      const { data, error } = await supabase
        .from('studio_domain_whitelist')
        .insert([{
          studio_id: studioId,
          domain: domain
        }])
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error adding domain:', error);
      return { data: null, error };
    }
  },

  // Remove domain from whitelist
  async removeDomain(domainId) {
    try {
      const { data, error } = await supabase
        .from('studio_domain_whitelist')
        .delete()
        .eq('id', domainId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error removing domain:', error);
      return { data: null, error };
    }
  },

  // Get studio analytics
  async getStudioAnalytics(studioId, dateRange = 30) {
    try {
      // Get games for this studio
      const { data: games } = await supabase
        .from('games')
        .select('id')
        .eq('studio_id', studioId);

      if (!games || games.length === 0) {
        return { data: { totalPlays: 0, uniquePlayers: 0, averageSessionLength: 0, retentionRate: 0 }, error: null };
      }

      const gameIds = games.map(g => g.id);

      // Get analytics summary
      const { data: analytics, error } = await supabase
        .from('game_analytics_summary')
        .select('*')
        .in('game_id', gameIds)
        .gte('date', new Date(Date.now() - dateRange * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);

      if (error) throw error;

      // Calculate totals
      const totalPlays = analytics.reduce((sum, day) => sum + (day.total_sessions || 0), 0);
      const uniquePlayers = Math.max(...analytics.map(day => day.unique_players || 0), 0);
      const avgSessionLength = analytics.length > 0
        ? analytics.reduce((sum, day) => sum + (day.average_session_length || 0), 0) / analytics.length
        : 0;
      const retentionRate = analytics.length > 0
        ? analytics.reduce((sum, day) => sum + (day.retention_rate_1d || 0), 0) / analytics.length
        : 0;

      return {
        data: {
          totalPlays,
          uniquePlayers,
          averageSessionLength: Math.round(avgSessionLength),
          retentionRate: Math.round(retentionRate)
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching studio analytics:', error);
      return { data: null, error };
    }
  },

  // Get studio revenue data
  async getStudioRevenue(studioId) {
    try {
      // Get ad revenue
      const { data: adRevenue } = await supabase
        .from('ad_impressions')
        .select('studio_share')
        .eq('studio_id', studioId);

      // Get IAP revenue
      const { data: iapRevenue } = await supabase
        .from('in_game_purchases')
        .select('studio_revenue')
        .eq('studio_id', studioId)
        .eq('status', 'completed');

      // Get payouts
      const { data: payouts } = await supabase
        .from('studio_payouts')
        .select('*')
        .eq('studio_id', studioId)
        .order('created_at', { ascending: false });

      const totalAdRevenue = adRevenue?.reduce((sum, ad) => sum + (ad.studio_share || 0), 0) || 0;
      const totalIapRevenue = iapRevenue?.reduce((sum, iap) => sum + (iap.studio_revenue || 0), 0) || 0;
      const totalRevenue = totalAdRevenue + totalIapRevenue;

      // Calculate this month's revenue
      const thisMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
      const monthlyRevenue = payouts?.find(p => p.period_start.startsWith(thisMonth))?.payout_amount || 0;

      return {
        data: {
          totalRevenue,
          monthlyRevenue,
          adRevenue: totalAdRevenue,
          iapRevenue: totalIapRevenue,
          payouts: payouts || []
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching studio revenue:', error);
      return { data: null, error };
    }
  }
};
